<template>

    <div class="sway-form new-org-enrollment" v-if="$store.getters.isSuperAdmin">
        <h1>Create New Organization</h1>
        <app-form
                @submit="completeSetup"
                :saving="isCompletingSetup"
                :use-default-btn="false"
                submitButtonTitle="Create Organization"
        >
            <section class="form-section">
                <h3>Primary Admin User</h3>
                <div class="field-grid">
                    <app-text-field v-model="model.firstName" label="First Name" rules="required" />
                    <app-text-field v-model="model.lastName" label="Last Name" rules="required" />
                    <app-text-field v-model="model.jobTitle" label="Professional Designation" rules="required" />
                    <app-text-field v-model="model.email" label="Email" type="email" rules="required|email" />
                    <app-select
                            :items="professionalTypes"
                            label="Medical Credentials"
                            v-model="model.medicalCredentials"
                            rules="required"
                    />
                </div>
            </section>

            <section class="form-section">
                <h3>Organization Information</h3>
                <div class="field-grid">
                    <app-text-field v-model="model.orgName" label="Name" rules="required" />
                    <app-text-field v-model="model.orgPhone" label="Phone Number" :mask="'###-###-####'" rules="required" />
                    <app-text-field v-model="model.orgAdd1" label="Street Address" rules="required" errorLabel="Org Street Address" />
                    <app-text-field v-model="model.orgAdd2" label="Apt, Suite, Bldg (optional)" errorLabel="Org Address Line 2" />
                    <app-text-field v-model="model.orgCity" label="City" rules="required" errorLabel="Org City" />
                    <app-text-field v-model="model.orgState" label="State/Province" rules="required" errorLabel="Org State" />
                    <app-text-field v-model="model.orgZip" label="Postal Code" rules="required" errorLabel="Org Postal Code" />
                </div>
                <div class="checkbox-row">
                    <app-form-checkbox label="Internal Organization" v-model="model.isInternalOrg" />
                    <app-form-checkbox label="Has Custom Agreement" v-model="model.hasCustomAgreement" />
                </div>
            </section>

            <section class="form-section">
                <h3>Billing Information</h3>
                <div class="checkbox-row">
                    <app-form-checkbox label="Same as Organization Info" v-model="isSameAddress" />
                </div>
                <div class="field-grid">
                    <app-text-field v-model="model.billName" label="Billing Name" rules="required" />
                    <app-text-field v-model="model.billPhone" label="Billing Phone" :mask="'###-###-####'" rules="required" />
                    <app-text-field v-model="model.billEmail" label="Billing Email" type="email" rules="required|email" />
                    <app-text-field v-model="model.billAdd1" label="Street Address" rules="required" errorLabel="Billing Street Address" />
                    <app-text-field v-model="model.billAdd2" label="Apt, Suite, Bldg (optional)" errorLabel="Billing Address Line 2" />
                    <app-text-field v-model="model.billCity" label="City" rules="required" errorLabel="Billing City" />
                    <app-text-field v-model="model.billState" label="State/Province" rules="required" errorLabel="Billing State" />
                    <app-text-field v-model="model.billZip" label="Postal Code" rules="required" errorLabel="Billing Postal Code" />
                    <app-select
                            :items="countrySelectList"
                            label="Country"
                            v-model="model.billCountry"
                            item-text="text"
                            item-value="text"
                            rules="required"
                    />
                    <app-select
                            :items="billMethodSelectList"
                            label="Billing Method"
                            v-model="model.billMethod"
                            item-text="label"
                            item-value="id"
                            rules="required"
                    />
                </div>
                <div class="checkbox-row">
                    <app-form-checkbox label="Trial Status" v-model="model.isDemo" />
                    <app-form-checkbox label="Setup Organization As Tax Exempt" v-model="model.isTaxExempt" />
                </div>
            </section>

            <section class="form-section">
                <h3>Pricing Information</h3>
                <div class="field-grid">
                    <app-select
                            :items="organizationTypeIds"
                            label="Sale Type"
                            v-model="saleType"
                            rules="required"
                            item-text="label"
                            item-value="id"
                    />
                    <app-select
                            :items="businessTypeSelectList"
                            label="Business Type"
                            v-model="model.businessTypeId"
                            rules="required"
                            item-text="label"
                            item-value="id"
                    />
                    <app-select
                            :items="packageTierSelectList"
                            label="Package Type"
                            v-model="model.packageTierId"
                            rules="required"
                            item-text="text"
                            item-value="value"
                    />
                    <app-select
                            :items="billingCycleTypes"
                            label="Billing Cycle"
                            v-model="model.billingCycleTypeId"
                            rules="required"
                            item-text="text"
                            item-value="value"
                    />
                    <app-select
                            :items="distributorSelectList"
                            label="Sold By"
                            v-model="model.orgDistributorId"
                            rules="required"
                    />
                    <app-text-field v-model="model.billToOrgId" label="Org ID To Bill (optional)" type="number" />
                </div>
            </section>

            <div class="submit-row">
                <app-btn type="submit" color="primary" :loading="isCompletingSetup">
                    Create Organization
                </app-btn>
            </div>
        </app-form>
    </div>

</template>

<script>
import {billingCycleTypes, countryOptions, organizationBusinessTypeIds, clinicalPackageTiers, sportsPackageTiers, organizationTypeIds, billMethodIds, professionalTypes} from "@/js/shared/constants";
import {enrollOrganization} from "@/services/enrollment.service";
import {SET_SNACKBAR} from "@/store/mutation-types";
import {switchSelectedOrganization} from "@/services/userService";

export default {
    name: "NewOrgEnrollment",
    data() {
        return {
            model: {
                firstName: "",
                lastName: "",
                jobTitle: "",
                email: "",
                medicalCredentials: "",
                orgName: "",
                orgPhone: "",
                orgAdd1: "",
                orgAdd2: "",
                orgCity: "",
                orgState: "",
                orgZip: "",
                billName: "",
                billPhone: "",
                billEmail: "",
                billAdd1: "",
                billAdd2: "",
                billCity: "",
                billState: "",
                billZip: "",
                billCountry: "",
                billMethod: "",
                isDemo: false,
                isTaxExempt: false,
                billingCycleTypeId: 1,
                orgDistributorId: 48,
                saleType: 0,
                billToOrgId: null,
                isInternalOrg: false,
                hasCustomAgreement: false,
                businessTypeId: null,
                packageTierId: null,
            },
            
            saleType: 0,
            
            isCompletingSetup: false,
            
            isSameAddress: false,

            professionalTypes: professionalTypes,

            countrySelectList: countryOptions,

            billMethodSelectList: billMethodIds,

            businessTypeSelectList: organizationBusinessTypeIds,
            
            distributorSelectList: [
                { text: "Casey Paulk", value: 314},
                { text: "Doug Tauchen", value: 349},
                { text: "National Accounts", value: 48}
            ],

            organizationTypeIds: organizationTypeIds,
            
            billingCycleTypes: billingCycleTypes
        }
    },
    methods: {
        async completeSetup(){
            this.isCompletingSetup = true;
            
            if (!this.model.billToOrgId) // convert empty strings to null
                this.model.billToOrgId = null;

            var response = await enrollOrganization(this.model);
            
            this.isCompletingSetup = false;

            if (response && response.isSuccess && response.organization){
                this.$store.commit(SET_SNACKBAR, {
                    text: 'Organization Successfully Created',
                    color: 'success',
                    open: true
                });

                await switchSelectedOrganization(response.organization.id);
                await this.$router.push(`/dashboard?orgId=${response.organization.id}`);
            }
            else{
                // do nothing and let upstream methods handle this
                //this.$store.commit(SET_SNACKBAR, {
                //    text: 'Error Updating Organization',
                //    color: 'error',
                //    open: true
                //});
            }
        }
    },
    computed: {
        packageTierSelectList() {
            if (this.saleType === 3)
                return clinicalPackageTiers;
            else if (this.saleType === 2)
                return sportsPackageTiers;
            else
                return sportsPackageTiers.concat(clinicalPackageTiers)
        }
    },
    watch: {
        saleType: function() {
            if (this.saleType === 3 && !clinicalPackageTiers.find(x => x.value === this.model.packageTierId)){
                this.model.packageTierId = null;
            }
            else if (this.saleType === 2 && !sportsPackageTiers.find(x => x.value === this.model.packageTierId)){
                this.model.packageTierId = null;
            }
            this.model.saleType = this.saleType;
        },
        isSameAddress: function() {
            if (this.isSameAddress){
                this.model.billName = this.model.orgName;
                this.model.billPhone = this.model.orgPhone;
                this.model.billAdd1 = this.model.orgAdd1;
                this.model.billAdd2 = this.model.orgAdd2;
                this.model.billCity = this.model.orgCity;
                this.model.billState = this.model.orgState;
                this.model.billZip = this.model.orgZip;
            }
        }
    }
}
</script>

<style scoped>
.new-org-enrollment {
    padding: 16px 24px;
    max-width: 1100px;
}

.new-org-enrollment h1 {
    font-size: 1.5rem;
    margin-bottom: 12px;
}

.form-section {
    margin-bottom: 18px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e5e7eb;
}

.form-section:last-of-type {
    border-bottom: none;
}

.form-section h3 {
    font-size: 1rem;
    margin-bottom: 8px;
    color: #1f2937;
}

.field-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px 16px;
}

@media (max-width: 900px) {
    .field-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 600px) {
    .field-grid {
        grid-template-columns: 1fr;
    }
}

.checkbox-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 24px;
    margin-top: 6px;
}

.submit-row {
    display: flex;
    justify-content: center;
    margin-top: 16px;
    margin-bottom: 16px;
}
</style>